import React, { useState } from "react"

import { formatCentsToMoneyInputValue, MAX_MONEY_INPUT_CENTS_VALUE } from "@/utils/currencyFormatter"
import { regexList } from "@/utils/regex"

type UseMoneyInputCentsResult = {
	cents: number
	displayValue: string
	handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
	handlePaste: (event: React.ClipboardEvent<HTMLInputElement>) => void
	setCents: (cents: number) => void
}

const MONEY_INPUT_ALLOWED_CONTROL_KEYS = ["Tab", "Escape", "Enter"]

const useMoneyInputCents = (initialCents?: number | null): UseMoneyInputCentsResult => {
	const [cents, setCents] = useState<number>(initialCents ?? 0)

	const appendDigits = (digits: string) => {
		if (!digits) {
			return
		}

		setCents((previousCents) => {
			const shiftedCents = Number(`${previousCents}${digits}`)

			return Math.min(shiftedCents, MAX_MONEY_INPUT_CENTS_VALUE)
		})
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (MONEY_INPUT_ALLOWED_CONTROL_KEYS.includes(event.key)) {
			return
		}

		event.preventDefault()

		if (event.key === "Backspace" || event.key === "Delete") {
			setCents((previousCents) => Math.floor(previousCents / 10))
			return
		}

		if (regexList.is_only_digits.test(event.key)) {
			appendDigits(event.key)
		}
	}

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault()
		appendDigits(event.clipboardData.getData("text").replace(regexList.all_non_numbers, ""))
	}

	return { cents, displayValue: formatCentsToMoneyInputValue(cents), handleKeyDown, handlePaste, setCents }
}

export default useMoneyInputCents
